import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { styled } from '@linaria/react';
import { toggle } from 'opticks';
import { useIsReferralProgramEnabled } from '@daedalus/atlas/hooks/useIsReferralProgramEnabled';
import { Icon } from '@daedalus/atlas/Icon';
import { MenuItemComponent, useTrackMenuItemClick } from '@daedalus/core/src/analytics/hooks/useTrackMenuItemClick';
import { Team } from '@daedalus/core/src/analytics/types/Events';
import { trackLoginClick } from '@daedalus/core/src/auth/modules/actions';
import { selectIsAuthenticated, selectIsLoading } from '@daedalus/core/src/auth/modules/selectors';
import { AuthenticationFlows, SourceComponentType } from '@daedalus/core/src/auth/types/Auth';
import { Brand } from '@daedalus/core/src/brand/types';
import { checkIsBrandVio, getLocalizedHelpUrl } from '@daedalus/core/src/brand/utils';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getLanguageCode } from '@daedalus/core/src/localization/services/locale';
import { isReactNativeWebView } from '@daedalus/core/src/native';
import { useLogIn } from '../../../authentication/hooks/useLogIn';
import { ProfilePicture } from '../ProfilePicture';
import { AccountSideMenuItem } from './AccountSideMenuItem';
interface Props {
  brand: Brand;
  anonymousId: string;
}
const PARENT_URL = '/user';
const SideMenuWrapper = /*#__PURE__*/styled('ul')({
  name: "SideMenuWrapper",
  class: "sxbbwhz",
  propsAsIs: false
});
export const AccountSideMenu = ({
  brand,
  anonymousId
}: Props) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const isMobileApp = isReactNativeWebView();
  const logIn = useLogIn();
  const languageCode = useSelector(getLanguageCode);
  const isReferralProgramEnabled = useIsReferralProgramEnabled(brand);
  const {
    trackMenuItemClick
  } = useTrackMenuItemClick();
  const location = useLocation();
  const isHoldOutGroup = toggle('a87807d8-price-watch-toggle-holdout', true, false);
  const shouldEnableAlertsTab = !isHoldOutGroup && toggle('feature-alerts-management', false, true) && !isMobileApp;
  const onMenuItemClick = (menuItem: MenuItemComponent) => () => {
    trackMenuItemClick(menuItem, Team.Retention);
  };
  const isVio = checkIsBrandVio(brand);
  const onAccountClick = (e: React.MouseEvent) => {
    if (!isLoading && !isAuthenticated) {
      e.preventDefault();
      const component = SourceComponentType.AccountSideBar;
      dispatch(trackLoginClick(component));
      return logIn({
        sourceComponent: component,
        flow: AuthenticationFlows.Login,
        anonymousId
      });
    } else {
      trackMenuItemClick(MenuItemComponent.Account, Team.Retention);
    }
  };
  const onGetHelpClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackMenuItemClick(MenuItemComponent.GetHelp, Team.Retention);
    window.open(getLocalizedHelpUrl(brand, languageCode));
  };
  return <SideMenuWrapper>
      <ProfilePicture hasBottomIcon={isVio} />
      <AccountSideMenuItem onClick={onAccountClick} isActive={location.pathname === `${PARENT_URL}/account`} to={`${PARENT_URL}/account`} preFixIcon={<Icon name="UserCircle" />}>
        <FormattedMessageWrapper id="account" defaultMessage="Account" />
      </AccountSideMenuItem>
      {shouldEnableAlertsTab && <AccountSideMenuItem to={`${PARENT_URL}/alerts`} preFixIcon={<Icon name="Bell" />} isActive={location.pathname === `${PARENT_URL}/alerts`} onClick={onMenuItemClick(MenuItemComponent.AlertsManagement)}>
          <FormattedMessageWrapper id="alerts" defaultMessage="Alerts" />
        </AccountSideMenuItem>}
      {isReferralProgramEnabled && <AccountSideMenuItem to={`${PARENT_URL}/membership/referral`} preFixIcon={<Icon name="SpeechBubblePlus" />} isActive={location.pathname === `${PARENT_URL}/membership/referral`} onClick={onMenuItemClick(MenuItemComponent.Referral)}>
          <FormattedMessageWrapper id="membership.InviteFriendsToGems" defaultMessage="Invite Friends to {gems}" values={{
        gems: 'Vio.com'
      }} />
        </AccountSideMenuItem>}
      {isVio && <>
          <AccountSideMenuItem to={`${PARENT_URL}/membership`} preFixIcon={<Icon name="Sparkles" />} isActive={location.pathname === `${PARENT_URL}/membership`} onClick={onMenuItemClick(MenuItemComponent.GemsMembership)}>
            <FormattedMessageWrapper id="vioDeals.membership" defaultMessage="Vio Membership" />
          </AccountSideMenuItem>
          <AccountSideMenuItem to={`${PARENT_URL}/getHelp`} preFixIcon={<Icon name="Help" />} suffixIcon={<Icon name="Share" />} onClick={onGetHelpClick} isActive={false}>
            <FormattedMessageWrapper id="getHelp" defaultMessage="Get Help" />
          </AccountSideMenuItem>
        </>}
    </SideMenuWrapper>;
};

require("./AccountSideMenu.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AccountSideMenu.tsx");